import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import WebViewer from '@pdftron/pdfjs-express';
import FileSaver from 'file-saver';
import printJS from 'print-js';
import moment from 'moment';
import ReactTable from 'react-table';
import ApiService from '../../services/ApiService';
import LoadingPage from '../LoadingPage';
import FileService from '../../services/FileService';
import toastr from '../toastr';
import { modal } from '../modal/ModalEmitter';
import FlexAlert from '../FlexAlert';
import { useQuery, useQueryClient } from 'react-query';
import { Button } from 'react-bootstrap';
import { Api } from '../shopkeeper/typings/Api';

const reverse = s => [...s].reverse().join('');

const getQuoteData = ({ quoteId, dealId, templateId, options }) => {
  if (dealId) {
    return ApiService.get(`crm/deal/${dealId}/data`, { params: { templateId, options } }).then(response => response.data);
  } else {
    return ApiService.get(`crm/quote/${quoteId}/data`, { params: { templateId, options } }).then(response => response.data);
  }
};

const Overrides = ({ fields, template, dealId, quoteId }) => {
  const queryClient = useQueryClient();

  const queryKey = ['quoteData', { id: dealId ? dealId : quoteId, deal: !!dealId }];

  const { isLoading, data: quoteData } = useQuery(queryKey, () =>
    getQuoteData({ dealId, quoteId, templateId: template.id })
  );

  if (isLoading) {
    return <LoadingPage />;
  }

  const overrides = [];
  fields.forEach(field => {
    const initial = _.get(quoteData, ['initial', template.name, field.name]);
    const override = dealId
      ? _.get(quoteData, ['supplemental', field.name].join('.')) // deal doesn't have the template name
      : _.get(quoteData, ['supplemental', template.name, field.name].join('.'));
    if (override === undefined) {
      return;
    }
    overrides.push({ name: field.name, initial, override: String(override) });
  });

  if (!overrides.length) {
    return (
      <FlexAlert variant="success" icon="fad fa-info">
        <h5>Fields Not Modified</h5>
      </FlexAlert>
    );
  }

  const handleClear = field => {
    Api.delete('/crm/quote/supplemental', {
      params: {
        quoteId,
        dealId,
        templateId: template.id,
        field,
      },
    }).then(() => queryClient.invalidateQueries(queryKey));
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'Initial Value',
      accessor: 'initial',
    },
    {
      Header: 'Override Value',
      accessor: 'override',
    },
    {
      Header: 'Clear',
      accessor: 'name',
      Cell: ({ value }) => (
        <Button onClick={() => handleClear(value)} size="sm">
          Clear
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="mb-2 text-right">
        <Button variant="danger" onClick={handleClear}>
          Clear Overrides
        </Button>
      </div>
      <ReactTable
        className="-striped -highlight"
        columns={columns}
        data={overrides}
        showPagination={false}
        sortable
        style={{
          height: '420px',
        }}
      />
    </>
  );
};

const QuoteDocumentExpressPreview = ({ quoteId, templateId, dealId, options = {} }) => {
  const [template, setTemplate] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  const queryKey = ['quoteData', { id: dealId ? dealId : quoteId, deal: !!dealId }];

  const {
    isFetching,
    isLoading,
    data: quoteData,
  } = useQuery(queryKey, () => getQuoteData({ dealId, quoteId, templateId, options }));

  // keep track if webviewer started to prevent doing it twice
  const webViewerInitialized = useRef(false);

  // need to know when this done to so we can track real form changes
  const dataFieldsInitialized = useRef(false);

  const viewer = useRef(null);

  const initWebViewer = () => {
    WebViewer(
      {
        path: '/pdfexpress/',
        initialDoc: FileService.downloadPath(template.filename, 'templates'),
        licenseKey: 'FLQk29QX0AY1pVyw6Y1O',
      },
      viewer.current
    ).then(instance => {
      const { documentViewer, annotationManager, Annotations } = instance.Core;
      let pageCount = 1;
      // high print quality by default
      instance.UI.setPrintQuality(2);

      const { Feature } = instance;
      instance.disableFeatures([Feature.FilePicker, Feature.Annotations, Feature.Pan]);

      instance.UI.disableElements([
        'ribbons',
        'toolsHeader',
        'viewControlsButton',
        'selectToolButton',
        'toggleNotesButton',
        'menuButton',
        'searchButton',
        'panToolButton',
      ]);

      const reDrawCanvas = () => {
        for (let i = 0; i < pageCount; i++) {
          // Redraw canvas
          annotationManager.drawAnnotations(i + 1, null, true);
        }
      };

      const customStyles = widget => {
        // give fields that default blue background to be identified as editable
        if (widget instanceof Annotations.TextWidgetAnnotation) {
          let styles = {
            'background-color': 'rgb(50,100,255,0.2)',
            'font-family': 'Courier',
          };

          styles = {
            ...styles,
            ...combingStyles(widget),
          };

          return styles;
        }

        // new pdfjs express is coloring these by default
        if (
          widget instanceof Annotations.RadioButtonWidgetAnnotation ||
          widget instanceof Annotations.CheckButtonWidgetAnnotation
        ) {
          return {
            'background-color': 'transparent',
          };
        }
      };

      const combingStyles = widget => {
        const styles = {};
        if (widget instanceof Annotations.TextWidgetAnnotation) {
          const { fieldName } = widget;

          const [name, args] = fieldName.split('?');

          let comb = 0;
          const fieldParams = _.get(template.pdfOptions, ['fields', name]);
          if (template && fieldParams) {
            comb = fieldParams.comb;
          } else if (args) {
            const urlSearchParams = new URLSearchParams(args);
            const { combing } = Object.fromEntries(urlSearchParams.entries()) || {};
            comb = combing ? widget.getField().maxLen : 0;
          }

          if (comb) {
            const combWidth = (widget.Width / (comb !== -1 ? comb : 17)) * documentViewer.getZoom();

            // this is a hack to get combing to work, it's not perfect
            styles['letter-spacing'] = `calc(${combWidth}px - 1ch)`;
            styles['padding-left'] = '7px';
            styles['padding-right'] = '0';
            styles['font-family'] = 'monospace';
          }
        }
        return styles;
      };

      const customInfo = () => {
        const fields = [];
        annotationManager.getFieldManager().forEachField(field => fields.push(field));

        modal.open({
          hideFooter: true,
          hidePrint: true,
          title: 'Modified Fields',
          message: <Overrides fields={fields} template={template} quoteId={quoteId} dealId={dealId} />,
        });
      };

      const customPrint = async () => {
        const xfdf = await annotationManager.exportAnnotations({ links: false, widgets: false });

        ApiService.post('crm/template/archive', { quoteId, dealId, templateId, xfdf })
          .then(response => {
            printJS({ printable: response.data.pdfData.split('base64,')[1], type: 'pdf', base64: true });
          })
          .catch(err => {
            console.error(err);
            // PDF failed to download, try a different method

            // remove the background on input elements
            instance.UI.printInBackground({
              includeComments: false,
              includeAnnotations: false,
              onProgress: pageNumber => {
                // add custom styles back after last page
                if (pageNumber === pageCount) {
                  // this doesn't really work
                  setTimeout(() => {
                    Annotations.WidgetAnnotation.getCustomStyles = customStyles;
                    reDrawCanvas();
                  }, 1000);
                }
              },
            });
          });
      };

      const customDownload = async () => {
        const xfdf = await annotationManager.exportAnnotations({ links: false, widgets: false });

        ApiService.post('crm/template/archive', { quoteId, dealId, templateId, xfdf })
          .then(response => {
            FileSaver.saveAs(response.data.pdfData, response.data.filename);
          })
          .catch(err => {
            console.error(err);
            toastr.error('Download Failed. Try print to PDF');
          });
      };

      // Use our own print dialog
      instance.hotkeys.off(instance.hotkeys.Keys.CTRL_P);
      instance.hotkeys.on(instance.hotkeys.Keys.CTRL_P, e => {
        e.preventDefault();
        customPrint();
      });

      const infoButton = {
        type: 'actionButton',
        img: 'icon-header-chat-line',
        onClick: () => customInfo(),
        dataElement: 'customInfoButton',
      };

      // add custom buttons for printing/downloading
      const printButton = {
        type: 'actionButton',
        img: 'icon-header-print-line',
        onClick: () => customPrint(),
        dataElement: 'customPrintButton',
      };

      const downloadButton = {
        type: 'actionButton',
        img: 'icon-header-download',
        onClick: () => customDownload(),
        dataElement: 'customDownloadButton',
      };

      instance.UI.setHeaderItems(header => {
        header.push(infoButton);
        header.push(downloadButton);
        header.push(printButton);
      });

      Annotations.WidgetAnnotation.getCustomStyles = customStyles;

      documentViewer.addEventListener('documentLoaded', () => {
        pageCount = documentViewer.getPageCount();
        setPdfLoading(false);

        if (dataFieldsInitialized.current) {
          return;
        }

        documentViewer.getAnnotationsLoadedPromise().then(() => {
          const fieldManager = annotationManager.getFieldManager();
          fieldManager.forEachField(field => {
            const override = _.get(quoteData, ['supplemental', template.name, field.name].join('.'));

            let value = quoteData[field.name];

            if (override === undefined) {
              // only do value changes when no override value exists
              const fieldParams = _.get(template.pdfOptions, ['fields', field.name]);
              let dateFormat;

              if (fieldParams) {
                ({ dateFormat } = fieldParams);
                const { index, ref } = fieldParams;
                if (ref) {
                  value = quoteData[ref];
                }

                if (index !== undefined) {
                  if (index < 0) {
                    value = reverse(quoteData[ref])[Math.abs(index)] || '';
                  } else {
                    value = quoteData[ref][index] || '';
                  }
                }
              }

              // check for query string
              const [name, args] = field.name.split('?');

              if (args) {
                const urlSearchParams = new URLSearchParams(args);
                ({ dateFormat } = Object.fromEntries(urlSearchParams.entries()) || {});
                value = quoteData[name];
              }

              if (dateFormat && value.match(/\d{2}-\d{2}-\d{4}/)) {
                value = moment(value, 'MM-DD-YYYY').format(dateFormat);
              }
            }

            if (![null, undefined].includes(value)) {
              field.setValue(value);
            }
          });

          dataFieldsInitialized.current = true;
        });
      });

      annotationManager.addEventListener('fieldChanged', (field, value) => {
        if (!dataFieldsInitialized.current) {
          return;
        }
        handleBlur({ name: field.name, value });
      });
    });
  };

  useEffect(() => {
    ApiService.get(`crm/template/${templateId}`).then(response => {
      setTemplate(response.data);
    });
  }, []);

  useEffect(() => {
    if (!template || !quoteData || isLoading || isFetching || webViewerInitialized.current) {
      return;
    }

    webViewerInitialized.current = true;

    initWebViewer();
  }, [template, quoteData, isFetching, isLoading]);

  const handleBlur = ({ name, value }) => {
    return ApiService.put('crm/quote/supplemental', {
      quoteId,
      dealId,
      templateId,
      data: { [name]: value },
      partial: true,
    });
  };

  return (
    <div className="d-flex" style={{ height: '100%' }}>
      <div className="flex-grow-1">
        <div className="d-none d-md-block" style={{ height: '100%' }}>
          <div className="webviewer" style={{ height: '100vh' }} ref={viewer}>
            {!!(pdfLoading || isLoading) && <LoadingPage message="Loading PDF Preview" />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDocumentExpressPreview;
